import React, { useEffect, useState } from 'react';
import { Box, Flex, Heading, Text, Tabs, TabList, Tab, HStack, Icon } from '@chakra-ui/react';
import { FiLock } from 'react-icons/fi';
import { useSearchParams } from 'react-router-dom';
import Page from '../components/ui/Page';
import PermissionPreview from '../components/ui/PermissionPreview';
import SettingsApiKeysTab from '../components/SettingsApiKeysTab';
import SettingsUsersTab from '../components/SettingsUsersTab';
import SettingsProjectsTab from '../components/SettingsProjectsTab';
import OrganisationTab from '../components/settings/OrganisationTab';
import IntegrationsTab from '../components/settings/IntegrationsTab';
import { useOrg } from '../context/OrgContext';

const TAB_KEYS = ['organisation', 'projects', 'members', 'api', 'integrations'];

export default function SettingsPage() {
  const keys = TAB_KEYS;
  const [searchParams, setSearchParams] = useSearchParams();
  const [tabIndex, setTabIndex] = useState(() => Math.max(0, keys.indexOf((searchParams.get('tab') || '').toLowerCase())));
  const { isDeveloper } = useOrg();

  const TABS = [
    { key: 'organisation', label: 'Organisation', render: () => <OrganisationTab /> },
    { key: 'projects', label: 'Projects', render: () => <SettingsProjectsTab readOnly={isDeveloper} /> },
    { key: 'members', label: 'Members', render: () => <SettingsUsersTab readOnly={isDeveloper} /> },
    { key: 'api', label: 'API keys', render: () => <SettingsApiKeysTab readOnly={isDeveloper} /> },
    { key: 'integrations', label: 'Integrations', render: () => <IntegrationsTab /> },
  ];

  useEffect(() => {
    const idx = Math.max(0, keys.indexOf((searchParams.get('tab') || '').toLowerCase()));
    if (idx !== tabIndex) setTabIndex(idx);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleChange = (index) => {
    setTabIndex(index);
    const next = new URLSearchParams(searchParams);
    next.set('tab', keys[index]);
    setSearchParams(next, { replace: true });
  };

  return (
    <Page>
      <Flex align="flex-end" justify="space-between" gap={6} wrap="wrap" mb={4}>
        <Box>
          <Heading size="lg" fontWeight="800" letterSpacing="-0.5px" color="gray.900">Settings</Heading>
          <Text fontSize="sm" color="gray.500">Organisation settings for Acme QA</Text>
        </Box>
        <PermissionPreview />
      </Flex>

      {isDeveloper && (
        <HStack bg="gray.100" border="1px solid" borderColor="gray.200" borderRadius="12px" px={4} py={2.5} mb={4} spacing={2.5}>
          <Icon as={FiLock} color="gray.500" boxSize={4} />
          <Text fontSize="13px" color="gray.700">
            <b>View only.</b> Developers can review organisation settings but not change them.
          </Text>
        </HStack>
      )}

      <Tabs variant="unstyled" index={tabIndex} onChange={handleChange}>
        <TabList borderBottom="1px solid" borderColor="gray.200" mb={5} gap={1}>
          {TABS.map((t) => (
            <Tab
              key={t.key}
              fontSize="13px"
              fontWeight="600"
              color="gray.500"
              px={3.5}
              py={2.5}
              mb="-1px"
              borderBottom="2px solid"
              borderColor="transparent"
              _selected={{ color: 'brand.600', borderColor: 'brand.600', fontWeight: '700' }}
            >
              {t.label}
            </Tab>
          ))}
        </TabList>
      </Tabs>

      <Box>{TABS[tabIndex].render()}</Box>
    </Page>
  );
}
